import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Inbox, CalendarHeart, Mail, Phone, Trash2,
  Clock, CheckCircle2, XCircle, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ASSETS, PRACTITIONER } from "@/data/content";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PagesPanel from "@/components/admin/PagesPanel";
import ImagesPanel from "@/components/admin/ImagesPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";
import AvailabilityPanel from "@/components/admin/AvailabilityPanel";
import ContentPanel from "@/components/admin/ContentPanel";
import HelpPanel from "@/components/admin/HelpPanel";

function StatusPill({ status }) {
  const map = {
    new: "bg-brand-lavender-soft text-brand-lavender-dark",
    read: "bg-brand-accent text-brand-slate",
    pending: "bg-amber-100 text-amber-700",
    confirmed: "bg-green-100 text-green-700",
    declined: "bg-rose-100 text-rose-600",
  };
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-body capitalize ${map[status] || "bg-brand-accent text-brand-slate"}`}>
      {status}
    </span>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstVisit] = useState(() => {
    try { return !localStorage.getItem("ilh_onboarded"); } catch { return false; }
  });

  useEffect(() => {
    if (firstVisit) {
      try { localStorage.setItem("ilh_onboarded", "1"); } catch { /* ignore */ }
    }
  }, [firstVisit]);

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries"),
        api.get("/admin/bookings"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setBookings(b.data);
    } catch {
      toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const setBookingStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(`Booking marked ${status}.`);
    load();
  };
  const setEnquiryStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    load();
  };
  const del = async (kind, id) => {
    await api.delete(`/admin/${kind}/${id}`);
    toast.success("Deleted.");
    load();
  };

  if (!ready || !user) return null;

  const cards = stats ? [
    { label: "New enquiries", value: stats.new_enquiries, icon: Inbox },
    { label: "Total enquiries", value: stats.total_enquiries, icon: Mail },
    { label: "Pending bookings", value: stats.pending_bookings, icon: Clock },
    { label: "Confirmed bookings", value: stats.confirmed_bookings, icon: CheckCircle2 },
  ] : [];

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="bg-white border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="font-heading text-xl text-brand-ink leading-none">{PRACTITIONER.business}</p>
              <p className="font-body text-xs text-brand-slate mt-1">Practitioner dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 font-body text-sm text-brand-slate hover:text-brand-forest">
              <RefreshCw size={16} strokeWidth={1.5} /> Refresh
            </button>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 font-body text-sm text-brand-ink hover:bg-brand-accent">
              <LogOut size={16} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <h1 className="font-heading text-3xl text-brand-ink mb-1">Welcome back, Isabelle</h1>
        <p className="font-body text-brand-slate mb-8">Here's what's come in for you.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="rounded-3xl bg-white border border-brand-line p-6">
              <div className="flex items-center justify-between">
                <span className="h-10 w-10 rounded-full bg-brand-lavender-soft flex items-center justify-center">
                  <c.icon className="text-brand-lavender-dark" size={18} strokeWidth={1.5} />
                </span>
                <span className="font-heading text-4xl text-brand-ink">{c.value ?? 0}</span>
              </div>
              <p className="font-body text-sm text-brand-slate mt-3">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue={firstVisit ? "help" : "bookings"}>
          <TabsList className="bg-brand-accent/60 rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full data-[state=active]:bg-white">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-full data-[state=active]:bg-white">Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability" className="rounded-full data-[state=active]:bg-white">Availability</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages" className="rounded-full data-[state=active]:bg-white">Pages</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-full data-[state=active]:bg-white">Text</TabsTrigger>
            <TabsTrigger value="images" data-testid="tab-images" className="rounded-full data-[state=active]:bg-white">Images</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full data-[state=active]:bg-white">Settings</TabsTrigger>
            <TabsTrigger value="help" data-testid="tab-help" className="rounded-full data-[state=active]:bg-white">Help</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            {loading ? <p className="font-body text-brand-slate">Loading…</p> :
              bookings.length === 0 ? <p className="font-body text-brand-slate py-10 text-center">No bookings yet.</p> :
              <div className="space-y-4" data-testid="bookings-list">
                {bookings.map((b) => (
                  <div key={b.id} className="rounded-3xl bg-white border border-brand-line p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-heading text-xl text-brand-ink">{b.name}</h3>
                          <StatusPill status={b.status} />
                        </div>
                        <p className="font-body text-sm text-brand-slate">{b.session_type}</p>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-body text-sm text-brand-slate">
                          <span className="flex items-center gap-2"><CalendarHeart size={15} strokeWidth={1.5} /> {b.preferred_date} · {b.time_slot}</span>
                          <span className="flex items-center gap-2"><Mail size={15} strokeWidth={1.5} /> {b.email}</span>
                          {b.phone && <span className="flex items-center gap-2"><Phone size={15} strokeWidth={1.5} /> {b.phone}</span>}
                        </div>
                        {b.notes && <p className="mt-3 font-body text-sm text-brand-slate bg-brand-accent/40 rounded-xl p-3">{b.notes}</p>}
                        <p className="mt-2 font-body text-xs text-brand-slate/60">Requested {fmtDate(b.created_at)}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button onClick={() => setBookingStatus(b.id, "confirmed")} data-testid={`confirm-booking-${b.id}`} className="inline-flex items-center gap-2 rounded-full bg-brand-forest text-white px-4 py-2 font-body text-sm hover:bg-brand-forest-dark"><CheckCircle2 size={15} strokeWidth={1.5} /> Confirm</button>
                        <button onClick={() => setBookingStatus(b.id, "declined")} className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 font-body text-sm text-brand-slate hover:bg-brand-accent"><XCircle size={15} strokeWidth={1.5} /> Decline</button>
                        <button onClick={() => del("bookings", b.id)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-body text-sm text-rose-500 hover:bg-rose-50"><Trash2 size={15} strokeWidth={1.5} /> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
          </TabsContent>

          <TabsContent value="enquiries" className="mt-6">
            {loading ? <p className="font-body text-brand-slate">Loading…</p> :
              enquiries.length === 0 ? <p className="font-body text-brand-slate py-10 text-center">No enquiries yet.</p> :
              <div className="space-y-4" data-testid="enquiries-list">
                {enquiries.map((e) => (
                  <div key={e.id} className="rounded-3xl bg-white border border-brand-line p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="max-w-2xl">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-heading text-xl text-brand-ink">{e.name}</h3>
                          <StatusPill status={e.status} />
                        </div>
                        {e.subject && <p className="font-body text-sm text-brand-ink font-medium">{e.subject}</p>}
                        <p className="mt-2 font-body text-sm text-brand-slate leading-relaxed">{e.message}</p>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-body text-sm text-brand-slate">
                          <span className="flex items-center gap-2"><Mail size={15} strokeWidth={1.5} /> {e.email}</span>
                          {e.phone && <span className="flex items-center gap-2"><Phone size={15} strokeWidth={1.5} /> {e.phone}</span>}
                        </div>
                        <p className="mt-2 font-body text-xs text-brand-slate/60">Received {fmtDate(e.created_at)}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {e.status === "new" && <button onClick={() => setEnquiryStatus(e.id, "read")} data-testid={`read-enquiry-${e.id}`} className="inline-flex items-center gap-2 rounded-full bg-brand-forest text-white px-4 py-2 font-body text-sm hover:bg-brand-forest-dark"><CheckCircle2 size={15} strokeWidth={1.5} /> Mark read</button>}
                        <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 font-body text-sm text-brand-slate hover:bg-brand-accent"><Mail size={15} strokeWidth={1.5} /> Reply</a>
                        <button onClick={() => del("enquiries", e.id)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-body text-sm text-rose-500 hover:bg-rose-50"><Trash2 size={15} strokeWidth={1.5} /> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <AvailabilityPanel />
          </TabsContent>
          <TabsContent value="pages" className="mt-6">
            <PagesPanel />
          </TabsContent>
          <TabsContent value="content" className="mt-6">
            <ContentPanel />
          </TabsContent>
          <TabsContent value="images" className="mt-6">
            <ImagesPanel />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsPanel />
          </TabsContent>
          <TabsContent value="help" className="mt-6">
            <HelpPanel firstVisit={firstVisit} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
